/**
 * Derive a human-readable label for a workflow node.
 *
 * @module FlowDesigner/utils/nodeLabel
 *
 * Shared utility extracted from RunDetailsPanel and TestRunDetail so that
 * any code needing a step label (variables panel, run details, etc.) uses
 * a single implementation.
 */

import { NODE_TYPE_LABEL } from '@chatsdet/types';

interface MinimalNode {
  id: string;
  type?: string;
  data?: Record<string, unknown>;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/**
 * Return a short, human-readable label for a workflow node.
 *
 * Accepts any object shaped like `{ id, type?, data? }` so it works with
 * both `WorkflowNode` and `WorkflowNodeDto`.
 */
export function nodeLabel(nodes: MinimalNode[], nodeId: string): string {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return `Step ${nodeId.slice(0, 6)}`;

  const data = (node.data ?? {}) as Record<string, unknown>;

  if (node.type === 'login') {
    const actingAsLabel = typeof data.actingAsLabel === 'string' ? data.actingAsLabel : null;
    const credentialName = typeof data.credentialName === 'string' && data.credentialName.trim() ? data.credentialName : null;
    if (actingAsLabel ?? credentialName) return (actingAsLabel ?? credentialName)!;
    // Fallback for seed credential source before credentialName is derived
    if (data.credentialSource === 'from_seed') {
      const seedRef = data.seedCredentialRef as { dataKey?: unknown; name?: unknown } | undefined;
      return textOrNull(seedRef?.name) ?? textOrNull(seedRef?.dataKey) ?? NODE_TYPE_LABEL.login;
    }
    return NODE_TYPE_LABEL.login;
  }
  if (node.type === 'navigate') {
    const pageTitle = typeof data.pageTitle === 'string' ? data.pageTitle : null;
    const pageUrl = typeof data.pageUrl === 'string' ? data.pageUrl : null;
    return pageTitle ?? pageUrl ?? 'Navigate';
  }
  if (node.type === 'action') {
    const title = typeof data.customActionTitle === 'string' ? data.customActionTitle : null;
    const desc = typeof data.actionDescription === 'string' ? data.actionDescription : null;
    const kind = typeof data.actionKind === 'string' ? data.actionKind : null;
    return title ?? desc ?? kind ?? 'Action';
  }
  if (node.type === 'assertion') {
    const condition = typeof data.condition === 'string' ? data.condition : null;
    return condition ?? 'Assertion';
  }
  if (node.type === 'setup') {
    const name = typeof data.setupFlowName === 'string' ? data.setupFlowName : null;
    return name ? `Setup: ${name}` : 'Setup Flow';
  }
  if (node.type === 'call_flow') {
    const name = typeof data.flowName === 'string' ? data.flowName : null;
    return name ? `Run flow: ${name}` : 'Run a flow';
  }
  if (node.type === 'api_sequence') {
    const description = typeof data.description === 'string' ? data.description : null;
    const steps = Array.isArray(data.steps) ? data.steps : [];
    return description || `API Sequence (${steps.length} call${steps.length !== 1 ? 's' : ''})`;
  }
  if (node.type === 'wait') return 'Wait';
  if (node.type && node.type in NODE_TYPE_LABEL) {
    return NODE_TYPE_LABEL[node.type as keyof typeof NODE_TYPE_LABEL];
  }
  return node.type ?? `Step ${nodeId.slice(0, 6)}`;
}
